import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { ensureWelcomeStageRowId } from "../../functions/ensureWelcomeStageRowId";
import { resolvePlayerName } from "../../functions/resolvePlayerName";
import { getDbUserId, getExternalUserId } from "../../functions/multiplayUserId";
import "../fonts/breuer-headline.css";
import "../harry_potter/game.css";
import "./broomFlight.css";

import gameBg from "../images/stage2/bg_flappy.png";
import artboardPanel from "../images/stage2/Artboard 59.png";
import pillar1 from "../images/stage2/pillar1.png";
import pillar2 from "../images/stage2/pillar2.png";
import pillar3 from "../images/stage2/pillar3.png";
import pillar4 from "../images/stage2/pillar4.png";
import ring1 from "../images/stage2/ring1.png";
import ring2 from "../images/stage2/ring2.png";
import char1 from "../images/stage2/flapy/Char1.png";
import char2 from "../images/stage2/flapy/Char2.png";
import char3 from "../images/stage2/flapy/Char3.png";
import char4 from "../images/stage2/flapy/Char4.png";
import monsterH1 from "../images/shooting/h1.png";
import timeIcon from "../harry_potter/img/time.png";
import pointIcon from "../harry_potter/img/point.png";

const HOOP_COUNT = 11;
const HOOP_SPACING = 520;
const FIRST_HOOP_X = 720;
const LAST_HOOP_X = FIRST_HOOP_X + (HOOP_COUNT - 1) * HOOP_SPACING;
const FINISH_PILLAR_X = LAST_HOOP_X + 340;
const PASS_HALF_PX = 58;
const HOOP_DEPTH_ALIGN_PX = 96;
const HOOP_DEPTH_Y_EXTRA = 26;
const CATCH_RADIUS = 48;
const CATCH_MIN_HOOPS = 8;
const COIN_MIN_FOR_SNITCH = 8;
const MAX_FLIGHT_SCORE = 150;
const SNITCH_CATCH_BONUS = 40;
const COIN_POINTS = 10;

const FLIGHT_RULES_ITEMS = [
  "Tap or press Space to rise and control your flight",
  "Pass through hoops or collect coins to earn points (+10 each)",
  "The Golden Snitch can only be caught after collecting 8 coins",
  "Catch the Snitch to earn a bonus (+40)",
  "Complete the flight to view your final results and submit feedback",
];

// ─── Snitch trail config ───────────────────────────────────────────────────
const SNITCH_TRAIL_LEN = 28;
const SNITCH_TRAIL_STEP = 6;

// ─── Hoop glitter config ──────────────────────────────────────────────────
const GLITTER_PARTICLE_COUNT = 38;
const GLITTER_DURATION_MS = 820;

// ─── Ring image geometry ──────────────────────────────────────────────────
// The ring image contains the full pillar + ring oval as one tall graphic,
// bottom-anchored. The oval opening aligns with yCenter in arena space,
// so coins are placed directly at yCenter — no extra offset constants needed.

const MONSTER_CONFIG = [
  {
    id: "m1",
    anchorX: FIRST_HOOP_X + HOOP_SPACING * 2.15,
    yRatio: 0.46,
    ampX: 150,
    ampY: 0.22,
    freqX: 0.95,
    freqY: 1.22,
    phaseX: 0.35,
    phaseY: 0.9,
    phaseX2: 1.05,
    phaseY2: 0.55,
  },
  {
    id: "m2",
    anchorX: FIRST_HOOP_X + HOOP_SPACING * 3.85,
    yRatio: 0.4,
    ampX: 138,
    ampY: 0.2,
    freqX: 1.12,
    freqY: 1.18,
    phaseX: 2.1,
    phaseY: 1.65,
    phaseX2: 0.5,
    phaseY2: 0.95,
  },
  {
    id: "m3",
    anchorX: FIRST_HOOP_X + HOOP_SPACING * 5.35,
    yRatio: 0.48,
    ampX: 165,
    ampY: 0.24,
    freqX: 1.08,
    freqY: 1.4,
    phaseX: 1.2,
    phaseY: 0.2,
    phaseX2: 0.65,
    phaseY2: 1.15,
  },
  {
    id: "m4",
    anchorX: FIRST_HOOP_X + HOOP_SPACING * 7.25,
    yRatio: 0.44,
    ampX: 155,
    ampY: 0.21,
    freqX: 0.88,
    freqY: 1.32,
    phaseX: 0.75,
    phaseY: 2.4,
    phaseX2: 1.4,
    phaseY2: 0.3,
  },
];

const MONSTER_HIT_RX = 40;
const MONSTER_HIT_RY = 34;
const MONSTER_HIT_COOLDOWN_MS = 420;

const BG_FLAPPY_NATURAL_W = 10776;
const BG_FLAPPY_NATURAL_H = 1011;

const HOOP_Y_RATIOS = [0.38, 0.52, 0.32, 0.48, 0.58, 0.36, 0.5, 0.42, 0.55, 0.34, 0.46];

const PILLARS = [pillar1, pillar2, pillar3, pillar4];
const RINGS = [ring1, ring2];
const CHAR_CYCLE = [char1, char2, char3, char4];

function parseMmSsToSeconds(str) {
  const t = String(str ?? "").trim();
  if (!t || t === "0") return 0;
  const parts = t.split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 2) return Math.max(0, (parts[0] || 0) * 60 + (parts[1] || 0));
  if (parts.length === 3) {
    return Math.max(0, (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0));
  }
  return 0;
}

function formatMmSsFromSeconds(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function progressLine(coinsCollected) {
  if (coinsCollected >= 8) return "You're almost there…";
  if (coinsCollected >= 4) return "It's getting closer…";
  return "Collect coins to summon the Snitch…";
}

/**
 * Build hoop data.
 *
 * yCenter — gameplay collision Y. Varies per hoop via HOOP_Y_RATIOS.
 * coinY   — visual render Y. Always equals the fixed oval center of the
 *           ring image so coins sit inside the ring on every screen size.
 *
 * Ring images are bottom-anchored in CSS (bottom:0, height:72%, max 480px).
 * Their oval opening therefore sits at ONE fixed Y regardless of hoop index.
 * We must NOT move the ring/pillar elements — only place coins at that Y.
 *
 *   ringCssH      = min(arenaH × 0.72, 480)   [or 0.78/280 on short landscape]
 *   ringImgTopY   = arenaH − ringCssH
 *   ovalCenterY   = ringImgTopY + ringCssH × OVAL_CENTER_FRACTION
 *   OVAL_CENTER_FRACTION ≈ 0.175  (oval spans top ~35% of image; midpoint = 17.5%)
 */
function buildHoops(arenaH) {
  const topPad = arenaH * 0.08;
  const usable = arenaH - topPad - arenaH * 0.06;

  // Match the CSS ring height for the current breakpoint.
  const isShortLandscape =
    typeof window !== "undefined" &&
    window.matchMedia("(max-height: 500px) and (orientation: landscape)").matches;
  const ringCssH = isShortLandscape
    ? Math.min(arenaH * 0.78, 280)
    : Math.min(arenaH * 0.72, 480);

  // Fixed oval center — same for every hoop since all rings share the same CSS.
  const OVAL_CENTER_FRACTION = 0.175;
  const coinY = (arenaH - ringCssH) + ringCssH * OVAL_CENTER_FRACTION;

  return HOOP_Y_RATIOS.slice(0, HOOP_COUNT).map((ratio, i) => ({
    id: i,
    x: FIRST_HOOP_X + i * HOOP_SPACING,
    yCenter: topPad + ratio * usable,
    coinY,
    pillarSrc: PILLARS[i % PILLARS.length],
    ringSrc: RINGS[i % RINGS.length],
  }));
}

// ─── Glitter burst helper ─────────────────────────────────────────────────
function spawnGlitterBurst(canvas, cx, cy) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const particles = Array.from({ length: GLITTER_PARTICLE_COUNT }, () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = 55 + Math.random() * 120;
    const size = 3 + Math.random() * 5;
    const colors = ["#FFE066", "#FFF4B0", "#FFD700", "#FFAA00", "#FFFDE4", "#F5C842"];
    return {
      x: cx,
      y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 40,
      size,
      color: colors[Math.floor(Math.random() * colors.length)],
      alpha: 1,
      spin: (Math.random() - 0.5) * 12,
      rotation: Math.random() * Math.PI * 2,
      isStar: Math.random() > 0.5,
    };
  });

  const startTime = performance.now();

  function drawStar(ctx, x, y, r, rotation) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const outer = (i * Math.PI * 2) / 5 - Math.PI / 2;
      const inner = outer + Math.PI / 5;
      if (i === 0) ctx.moveTo(Math.cos(outer) * r, Math.sin(outer) * r);
      else ctx.lineTo(Math.cos(outer) * r, Math.sin(outer) * r);
      ctx.lineTo(Math.cos(inner) * (r * 0.42), Math.sin(inner) * (r * 0.42));
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  function animate(now) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / GLITTER_DURATION_MS, 1);
    const dt = 0.016;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const p of particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 160 * dt;
      p.rotation += p.spin * dt;
      p.alpha = Math.max(0, 1 - progress * 1.2);
      const size = p.size * (1 - progress * 0.4);

      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = p.color;

      if (p.isStar) {
        drawStar(ctx, p.x, p.y, size, p.rotation);
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, size * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.globalAlpha = 1;

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  requestAnimationFrame(animate);
}

function BroomFlightGame() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);

  const {
    wandId,
    points: stage1Points = 0,
    time: stage1Time = "",
    reportId: reportIdFromNav,
    stageId: stageIdFromNav,
  } = location.state ?? {};

  const storedUserData = useMemo(() => {
    try {
      return (
        JSON.parse(localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}") ||
        {}
      );
    } catch {
      return {};
    }
  }, []);

  const reportRoleForApi =
    storedUserData.user_type ||
    storedUserData.userType ||
    storedUserData.role ||
    (storedUserData.source === "DEMO" ? "demobypass" : null) ||
    storedUserData.source ||
    "ORG_USER";
  const sidLc = String(storedUserData?.sessionId ?? "").toLowerCase();
  const oidLc = String(storedUserData?.organizationId ?? "").toLowerCase();
  const isDemoBypass =
    String(reportRoleForApi).toLowerCase() === "demobypass" ||
    storedUserData?.source === "DEMO" ||
    (sidLc === "demobypass" && oidLc === "demobypass");

  const stageRef = useRef(null);
  const worldRef = useRef(null);
  const worldRingsRef = useRef(null);
  const hoopFrontsRef = useRef(null);
  const playerRef = useRef(null);
  const playerImgRef = useRef(null);
  const snitchRef = useRef(null);
  const monsterRefs = useRef([]);
  const hoopWrapRefs = useRef([]);
  const hoopFrontWrapRefs = useRef([]);

  const glitterCanvasRef = useRef(null);
  const snitchTrailCanvasRef = useRef(null);
  const snitchTrailRef = useRef([]);
  const snitchLastRecordedRef = useRef({ x: -9999, y: -9999 });

  const reportIdRef = useRef(reportIdFromNav || null);
  const stageRowIdRef = useRef(
    stageIdFromNav != null && String(stageIdFromNav).trim() !== ""
      ? String(stageIdFromNav).trim()
      : null
  );
  const combinedTimeStrRef = useRef("");
  const elapsedFlightRef = useRef(0);
  const finalizeFlightRef = useRef(() => {});
  const [baseTimeSec, setBaseTimeSec] = useState(() => parseMmSsToSeconds(stage1Time));
  const baseTimeSecRef = useRef(parseMmSsToSeconds(stage1Time));

  const [phase, setPhase] = useState("intro");
  const [introStep, setIntroStep] = useState(0);
  const [hoops, setHoops] = useState([]);
  const [flightScore, setFlightScore] = useState(0);
  const [hoopsScored, setHoopsScored] = useState(0);
  const [hudLine, setHudLine] = useState("Keep going…");
  const [toast, setToast] = useState(null);
  const [catchFlash, setCatchFlash] = useState(false);
  const [playerHitPulse, setPlayerHitPulse] = useState(0);
  const [elapsedFlight, setElapsedFlight] = useState(0);
  const [resultLines, setResultLines] = useState({ primary: "", secondary: "", tertiary: "" });
  const [rulesModalOpen, setRulesModalOpen] = useState(true);

  const simRef = useRef({
    camera: 0,
    playerY: 0,
    playerVy: 0,
    running: false,
    speed: 208,
    gravity: 940,
    flap: -395,
    arenaH: 480,
    arenaW: 800,
    playerX: 200,
    crossed: [],
    scoredMask: [],
    coinCollected: [],
    snitchCaught: false,
    flightOver: false,
    snitchUnlockAnnounced: false,
    snitchUnlockTime: null,
    animFrame: 0,
    lastTs: 0,
    timeAcc: 0,
  });

  const coinRefs = useRef([]);

  const finishOnceRef = useRef(false);
  const lastSnitchCaughtRef = useRef(false);
  const hoopYRef = useRef([]);
  // Store coinY per hoop for collision detection in the game loop
  const coinYRef = useRef([]);
  const lastMonsterHitAtRef = useRef(0);

  useEffect(() => {
    dispatch(setBackButtonUrl("/stage2"));
  }, [dispatch]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("broom-flight-active");
    document.body.classList.add("broom-flight-active", "game-active");
    root.style.setProperty("--broom-flight-bg-url", `url("${gameBg}")`);
    root.style.setProperty("--broom-flight-bg-x", "0px");
    return () => {
      root.classList.remove("broom-flight-active");
      document.body.classList.remove("broom-flight-active", "game-active");
      root.style.removeProperty("--broom-flight-bg-url");
      root.style.removeProperty("--broom-flight-bg-x");
    };
  }, [gameBg]);

  useEffect(() => {
    if (isDemoBypass || !adminToken) return;
    const uid = getDbUserId(storedUserData);
    const sidRaw = storedUserData.sessionId;
    const oidRaw = storedUserData.organizationId;
    if (!uid || !sidRaw || !oidRaw) return;

    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (!backendBase) return;

    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: uid, sessionId: sidRaw, organizationId: oidRaw }),
        });
        const data = await res.json().catch(() => ({}));
        const extRid = data?.report?.reportId;
        if (extRid != null && extRid !== "") {
          reportIdRef.current = String(extRid);
        }
        const rowId = data?.report?.id;
        if (rowId != null && rowId !== "") {
          stageRowIdRef.current = String(rowId);
        }
        const rowTime = data?.report?.time;
        if (rowTime != null && String(rowTime).trim() !== "") {
          const sec = parseMmSsToSeconds(String(rowTime));
          setBaseTimeSec(sec);
        }
      } catch (e) {
        console.error("broom fetchReport:", e);
      }
    })();
  }, [adminToken, isDemoBypass, storedUserData]);

  useEffect(() => {
    elapsedFlightRef.current = elapsedFlight;
  }, [elapsedFlight]);

  useLayoutEffect(() => {
    baseTimeSecRef.current = baseTimeSec;
  }, [baseTimeSec]);

  useEffect(() => {
    if (phase !== "fly") return;
    const id = window.setInterval(() => setElapsedFlight((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== "intro") return;
    if (introStep >= 2) return;
    const ms = introStep === 0 ? 2000 : 2200;
    const t = window.setTimeout(() => setIntroStep((s) => s + 1), ms);
    return () => window.clearTimeout(t);
  }, [phase, introStep]);

  const layoutArena = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = r.width;
    const h = r.height;
    if (h < 80) return;
    const s = simRef.current;
    s.arenaW = w;
    s.arenaH = h;
    s.playerX = w * 0.26;
    const built = buildHoops(h);
    hoopYRef.current = built.map((x) => x.yCenter);
    coinYRef.current = built.map((x) => x.coinY);
    setHoops(built);
    if (s.playerY <= 0 || s.playerY > h) {
      s.playerY = h * 0.45;
    }
    if (glitterCanvasRef.current) {
      glitterCanvasRef.current.width = w;
      glitterCanvasRef.current.height = h;
    }
    if (snitchTrailCanvasRef.current) {
      snitchTrailCanvasRef.current.width = w;
      snitchTrailCanvasRef.current.height = h;
    }
  }, []);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    layoutArena();
    const ro = new ResizeObserver(() => layoutArena());
    ro.observe(el);
    return () => ro.disconnect();
  }, [layoutArena]);

  const flap = useCallback(() => {
    const s = simRef.current;
    if (!s.running || s.flightOver) return;
    s.playerVy = s.flap;
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.code === "ArrowUp" || e.code === "KeyW") {
        e.preventDefault();
        if (phase === "intro" && introStep >= 2) {
          setPhase("fly");
        } else if (phase === "fly") {
          flap();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, introStep, flap]);

  const onStagePointer = () => {
    if (phase === "intro" && introStep >= 2) {
      setPhase("fly");
      return;
    }
    if (phase === "fly") {
      flap();
    }
  };

  const finalizeFlight = useCallback(
    async (snitchCaught, scoredCount) => {
      if (finishOnceRef.current) return;
      finishOnceRef.current = true;
      lastSnitchCaughtRef.current = snitchCaught;

      const hoopPts = Math.min(scoredCount * COIN_POINTS, MAX_FLIGHT_SCORE - SNITCH_CATCH_BONUS);
      const totalFlight = snitchCaught ? Math.min(hoopPts + SNITCH_CATCH_BONUS, MAX_FLIGHT_SCORE) : hoopPts;
      setFlightScore(totalFlight);
      setHoopsScored(scoredCount);

      if (snitchCaught) {
        setResultLines({
          primary: "You caught the Golden Snitch...",
          secondary: "A true Seeker rises.",
          tertiary: "",
        });
      } else {
        setResultLines({
          primary: "The Snitch slipped away...",
          secondary: "Even the best must chase again.",
          tertiary: "",
        });
      }

      const totalPoints = (typeof stage1Points === "number" ? stage1Points : 0) + totalFlight;
      const stage2Sec = Math.max(0, Math.floor(elapsedFlightRef.current));
      const combinedTimeStr = formatMmSsFromSeconds(baseTimeSecRef.current + stage2Sec);
      combinedTimeStrRef.current = combinedTimeStr;

      try {
        if (!isDemoBypass) {
          const uid = getDbUserId(storedUserData);
          const externalUid = getExternalUserId(storedUserData);
          const sidRaw = storedUserData.sessionId;
          const oidRaw = storedUserData.organizationId;
          const role = reportRoleForApi;
          const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

          if (backendBase && uid && sidRaw && oidRaw && adminToken) {
            const rowId = await ensureWelcomeStageRowId({
              backendBase,
              adminToken,
              storedUserData,
              playerName: resolvePlayerName(storedUserData),
              user_type: role,
            });
            if (rowId) stageRowIdRef.current = rowId;
          }
          if (backendBase && stageRowIdRef.current && uid && sidRaw && oidRaw && adminToken) {
            try {
              const stage1Num = typeof stage1Points === "number" ? stage1Points : 0;
              const s2Res = await fetch(`${backendBase}/welcomeStage2Complete`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                  userId: uid,
                  sessionId: sidRaw,
                  organizationId: oidRaw,
                  stageId: stageRowIdRef.current,
                  stage1: stage1Num,
                  stage2_score: totalFlight,
                  total_score: totalPoints,
                  time: combinedTimeStr,
                  gameover: 2,
                }),
              });
              if (!s2Res.ok) {
                const errTxt = await s2Res.text().catch(() => "");
                console.error("welcomeStage2Complete HTTP", s2Res.status, errTxt);
              }
            } catch (stageErr) {
              console.error("welcomeStage2Complete:", stageErr);
            }
          }

          const res = await sendReport({
            ...(reportIdRef.current ? { reportId: reportIdRef.current } : {}),
            sessionId: sidRaw,
            organizationId: oidRaw,
            role,
            token: storedUserData.token || "",
            points: totalPoints,
            time: combinedTimeStr,
            gameId: storedUserData.gameId,
            name: resolvePlayerName(storedUserData),
            userId: externalUid,
          });
          const newRid = extractReportIdFromResponse(res);
          if (newRid && !reportIdRef.current) {
            reportIdRef.current = String(newRid);
          }
        }
      } catch (err) {
        console.error("broom sendReport:", err);
      }

      setPhase("result");
    },
    [adminToken, isDemoBypass, navigate, reportRoleForApi, stage1Points, storedUserData]
  );

  useLayoutEffect(() => {
    finalizeFlightRef.current = finalizeFlight;
  }, [finalizeFlight]);

  useEffect(() => {
    if (phase !== "fly") return;
    const s = simRef.current;
    s.running = true;
    s.flightOver = false;
    s.camera = 0;
    s.playerVy = 0;
    s.snitchCaught = false;
    s.snitchUnlockAnnounced = false;
    s.animFrame = 0;
    s.lastTs = performance.now();
    s.timeAcc = 0;
    finishOnceRef.current = false;
    lastSnitchCaughtRef.current = false;
    lastMonsterHitAtRef.current = 0;
    setPlayerHitPulse(0);
    s.crossed = new Array(HOOP_COUNT).fill(false);
    s.scoredMask = new Array(HOOP_COUNT).fill(false);
    s.coinCollected = new Array(HOOP_COUNT).fill(false);
    s.snitchUnlockTime = null;
    layoutArena();
    setElapsedFlight(0);
    setFlightScore(0);
    setHoopsScored(0);
    setHudLine(progressLine(0));

    snitchTrailRef.current = [];
    snitchLastRecordedRef.current = { x: -9999, y: -9999 };

    let raf = 0;
    let scoredLocal = 0;
    let coinsCollectedLocal = 0;

    function drawSnitchTrail(trailPoints, canvasEl) {
      if (!canvasEl) return;
      const ctx = canvasEl.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
      const len = trailPoints.length;
      if (len < 2) return;
      for (let i = 0; i < len; i++) {
        const t = i / (len - 1);
        const alpha = t * 0.72;
        const radius = 2 + t * 5;
        const p = trailPoints[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius + 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 220, 60, ${alpha * 0.35})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, ${180 + Math.floor(t * 60)}, ${Math.floor(t * 30)}, ${alpha})`;
        ctx.fill();
      }
    }

    const loop = (now) => {
      const dt = Math.min(0.034, (now - s.lastTs) / 1000) || 0.016;
      s.lastTs = now;

      if (!s.running || s.flightOver) {
        raf = requestAnimationFrame(loop);
        return;
      }

      s.animFrame += dt;
      s.playerVy += s.gravity * dt;
      s.playerY += s.playerVy * dt;
      const margin = 22;
      if (s.playerY < margin) {
        s.playerY = margin;
        s.playerVy = 0;
      }
      if (s.playerY > s.arenaH - margin) {
        s.playerY = s.arenaH - margin;
        s.playerVy = 0;
      }

      s.camera += s.speed * dt;

      const px = s.playerX;
      const py = s.playerY;
      const pwx = s.camera + px;

      const yList = hoopYRef.current;
      for (let i = 0; i < HOOP_COUNT; i++) {
        const hx = FIRST_HOOP_X + i * HOOP_SPACING;
        const hy = yList[i] ?? s.arenaH * 0.45;
        const sx = hx - s.camera;

        if (!s.crossed[i] && sx <= px) {
          s.crossed[i] = true;
          const dy = Math.abs(py - hy);
          if (dy < PASS_HALF_PX) {
            s.scoredMask[i] = true;
            scoredLocal += 1;
            const hw = hoopWrapRefs.current[i];
            const hf = hoopFrontWrapRefs.current[i];
            [hw, hf].forEach((el) => {
              if (el) el.classList.remove("broom-flight-hoop-wrap--pass-flash");
            });
            requestAnimationFrame(() => {
              [hw, hf].forEach((el) => {
                if (el) el.classList.add("broom-flight-hoop-wrap--pass-flash");
              });
            });
          }
        }
      }

      let inHoopDepth = false;
      for (let i = 0; i < HOOP_COUNT; i++) {
        const hx = FIRST_HOOP_X + i * HOOP_SPACING;
        const hy = yList[i] ?? s.arenaH * 0.45;
        const hoopScreenX = hx - s.camera;
        if (
          Math.abs(hoopScreenX - px) < HOOP_DEPTH_ALIGN_PX &&
          Math.abs(py - hy) < PASS_HALF_PX + HOOP_DEPTH_Y_EXTRA
        ) {
          inHoopDepth = true;
          break;
        }
      }
      if (playerRef.current) {
        playerRef.current.classList.toggle("broom-flight-player--in-hoop", inHoopDepth);
      }
      if (hoopFrontsRef.current) {
        hoopFrontsRef.current.classList.toggle("broom-flight-hoop-fronts--player-inside", inHoopDepth);
      }

      const nowMs = performance.now();
      const tAnim = s.animFrame;
      const vmargin = 30;
      for (let mi = 0; mi < MONSTER_CONFIG.length; mi++) {
        const cfg = MONSTER_CONFIG[mi];
        const ampY = cfg.ampY * s.arenaH;
        const wx =
          Math.sin(tAnim * cfg.freqX + cfg.phaseX) * cfg.ampX +
          Math.cos(tAnim * cfg.freqX * 0.71 + cfg.phaseX2) * (cfg.ampX * 0.42);
        const wy =
          Math.cos(tAnim * cfg.freqY + cfg.phaseY) * ampY +
          Math.sin(tAnim * cfg.freqY * 1.09 + cfg.phaseY2) * (ampY * 0.48);
        const mx = cfg.anchorX + wx;
        const baseY = cfg.yRatio * s.arenaH;
        const my = Math.max(vmargin, Math.min(s.arenaH - vmargin, baseY + wy));
        const mel = monsterRefs.current[mi];
        if (mel) {
          mel.style.left = `${mx}px`;
          mel.style.top = `${my}px`;
        }
        if (
          Math.abs(pwx - mx) < MONSTER_HIT_RX &&
          Math.abs(py - my) < MONSTER_HIT_RY &&
          nowMs - lastMonsterHitAtRef.current >= MONSTER_HIT_COOLDOWN_MS
        ) {
          lastMonsterHitAtRef.current = nowMs;
          setPlayerHitPulse((n) => n + 1);
          break;
        }
      }

      const t = s.animFrame;
      const zig = Math.sin(t * 2.05) * 36 + Math.sin(t * 3.4 + 0.7) * 20;
      const loopY = Math.sin(t * 1.48) * 44 + Math.cos(t * 0.92 + 0.35) * 16;

      let snitchX = px, snitchY = s.arenaH * 0.38;
      if (!s.snitchCaught) {
        if (coinsCollectedLocal < COIN_MIN_FOR_SNITCH) {
          const leadBase = 195 + (COIN_MIN_FOR_SNITCH - coinsCollectedLocal) * 24;
          snitchX = px + leadBase + zig;
          snitchY = s.arenaH * 0.38 + loopY;
          // Keep snitch within visible bounds on narrow mobile screens
          snitchX = Math.max(px + 40, Math.min(s.arenaW - 20, snitchX));
          snitchY = Math.max(20, Math.min(s.arenaH - 20, snitchY));
        } else {
          const unlockElapsed = t - (s.snitchUnlockTime ?? t);
          if (!s.snitchUnlockTime) s.snitchUnlockTime = t;
          const orbitRadius = Math.max(28, 160 - unlockElapsed * 33);
          const orbitSpeed = 2.6 + unlockElapsed * 0.18;
          snitchX = px + Math.cos(t * orbitSpeed) * orbitRadius + zig * 0.3;
          snitchY = py + Math.sin(t * orbitSpeed * 1.15) * orbitRadius * 0.7 + loopY * 0.25;
          snitchX = Math.max(px - 20, Math.min(s.arenaW - 20, snitchX));
          snitchY = Math.max(20, Math.min(s.arenaH - 20, snitchY));
        }

        if (snitchRef.current) {
          snitchRef.current.style.left = `${snitchX}px`;
          snitchRef.current.style.top = `${snitchY}px`;
        }

        const last = snitchLastRecordedRef.current;
        const dx = snitchX - last.x;
        const dy2 = snitchY - last.y;
        if (Math.hypot(dx, dy2) >= SNITCH_TRAIL_STEP) {
          const trail = snitchTrailRef.current;
          trail.push({ x: snitchX, y: snitchY });
          if (trail.length > SNITCH_TRAIL_LEN) trail.shift();
          snitchLastRecordedRef.current = { x: snitchX, y: snitchY };
          drawSnitchTrail(trail, snitchTrailCanvasRef.current);
        }
      }

      // ── COIN COLLECTION ──────────────────────────────────────────────────
      // Use coinY (visual center of ring oval) for both rendering AND collision.
      const COIN_COLLECT_R = 50;
      const coinYList = coinYRef.current;
      for (let i = 0; i < HOOP_COUNT; i++) {
        if (s.coinCollected[i]) continue;
        const hx = FIRST_HOOP_X + i * HOOP_SPACING;
        // Use visual ring oval center for collision
        const cy = coinYList[i] ?? yList[i] ?? s.arenaH * 0.45;
        const coinScreenX = hx - s.camera;
        if (
          Math.abs(coinScreenX - px) < COIN_COLLECT_R &&
          Math.abs(py - cy) < COIN_COLLECT_R
        ) {
          s.coinCollected[i] = true;
          coinsCollectedLocal += 1;

          const coinScore = Math.min(coinsCollectedLocal * COIN_POINTS, MAX_FLIGHT_SCORE - SNITCH_CATCH_BONUS);
          setFlightScore(coinScore);
          setHoopsScored(coinsCollectedLocal);
          setHudLine(progressLine(coinsCollectedLocal));

          const cel = coinRefs.current[i];
          if (cel) cel.classList.add("broom-flight-coin--collected");
          spawnGlitterBurst(glitterCanvasRef.current, px, py);

          if (coinsCollectedLocal === COIN_MIN_FOR_SNITCH && !s.snitchUnlockAnnounced) {
            s.snitchUnlockAnnounced = true;
            setToast("✨ 8 coins! The Golden Snitch appears!");
            window.setTimeout(() => setToast(null), 3200);
          }
        }
      }

      if (
        !s.flightOver &&
        coinsCollectedLocal >= COIN_MIN_FOR_SNITCH &&
        !s.snitchCaught &&
        Math.hypot(snitchX - px, snitchY - py) < CATCH_RADIUS
      ) {
        s.snitchCaught = true;
        const coinPts = Math.min(coinsCollectedLocal * COIN_POINTS, MAX_FLIGHT_SCORE - SNITCH_CATCH_BONUS);
        const pts = Math.min(coinPts + SNITCH_CATCH_BONUS, MAX_FLIGHT_SCORE);
        setFlightScore(pts);
        setCatchFlash(true);
        spawnGlitterBurst(glitterCanvasRef.current, snitchX, snitchY);
        window.setTimeout(() => setCatchFlash(false), 650);
        setToast("✨ Snitch caught! Fly to the finish!");
        window.setTimeout(() => setToast(null), 3000);
        if (snitchRef.current) snitchRef.current.style.display = "none";
        if (snitchTrailCanvasRef.current) {
          const ctx = snitchTrailCanvasRef.current.getContext("2d");
          if (ctx) ctx.clearRect(0, 0, snitchTrailCanvasRef.current.width, snitchTrailCanvasRef.current.height);
        }
      }

      const allHoopsPassed = s.crossed.length === HOOP_COUNT && s.crossed.every(Boolean);
      const reachedFinishPillar = FINISH_PILLAR_X - s.camera <= px;
      if (!s.flightOver && allHoopsPassed && reachedFinishPillar) {
        s.flightOver = true;
        s.running = false;
        finalizeFlightRef.current?.(s.snitchCaught, coinsCollectedLocal);
      }

      if (!s.flightOver && s.camera > FINISH_PILLAR_X - px + 2200) {
        s.flightOver = true;
        s.running = false;
        finalizeFlightRef.current?.(s.snitchCaught, coinsCollectedLocal);
      }

      const cam = `translate3d(${-s.camera}px,0,0)`;
      if (worldRef.current) {
        worldRef.current.style.transform = cam;
      }
      if (worldRingsRef.current) {
        worldRingsRef.current.style.transform = cam;
      }
      if (hoopFrontsRef.current) {
        hoopFrontsRef.current.style.transform = cam;
      }
      const posH = Math.max(1, document.documentElement.clientHeight || s.arenaH);
      const bgTileW = (BG_FLAPPY_NATURAL_W / BG_FLAPPY_NATURAL_H) * posH;
      const shift = (s.camera * 0.14) % bgTileW;
      document.documentElement.style.setProperty("--broom-flight-bg-x", `${-shift}px`);
      if (playerRef.current) {
        playerRef.current.style.left = `${px}px`;
        playerRef.current.style.top = `${py}px`;
      }
      if (playerImgRef.current) {
        const frame = Math.floor(s.animFrame * 5.5) % CHAR_CYCLE.length;
        playerImgRef.current.src = CHAR_CYCLE[frame];
      }

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      s.running = false;
      if (playerRef.current) {
        playerRef.current.classList.remove("broom-flight-player--in-hoop");
      }
      if (hoopFrontsRef.current) {
        hoopFrontsRef.current.classList.remove("broom-flight-hoop-fronts--player-inside");
      }
      if (snitchTrailCanvasRef.current) {
        const ctx = snitchTrailCanvasRef.current.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, snitchTrailCanvasRef.current.width, snitchTrailCanvasRef.current.height);
      }
      if (glitterCanvasRef.current) {
        const ctx = glitterCanvasRef.current.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, glitterCanvasRef.current.width, glitterCanvasRef.current.height);
      }
    };
  }, [phase, layoutArena]);

  const goThankYou = useCallback(() => {
    const totalPoints = (typeof stage1Points === "number" ? stage1Points : 0) + flightScore;
    const timeOut =
      combinedTimeStrRef.current && String(combinedTimeStrRef.current).trim() !== ""
        ? String(combinedTimeStrRef.current).trim()
        : formatMmSsFromSeconds(
            baseTimeSecRef.current + Math.max(0, Math.floor(elapsedFlightRef.current))
          );
    navigate("/thankyou", {
      replace: true,
      state: {
        points: totalPoints,
        time: timeOut,
        spellScore: typeof stage1Points === "number" ? stage1Points : 0,
        flightScore,
        stageId: stageRowIdRef.current ?? undefined,
        stage2Hoops: hoopsScored,
        stage2SnitchCaught: lastSnitchCaughtRef.current,
      },
    });
  }, [flightScore, hoopsScored, navigate, stage1Points]);

  useEffect(() => {
    if (phase !== "result") return;
    const t = window.setTimeout(() => {
      goThankYou();
    }, 2600);
    return () => window.clearTimeout(t);
  }, [goThankYou, phase]);

  if (!wandId) {
    return <Navigate to="/wand" replace />;
  }

  const introCopy =
    introStep === 0 ? "There it is…" : introStep === 1 ? "The Golden Snitch…" : "Tap or press Space to rise.";

  const stage1Num = typeof stage1Points === "number" ? stage1Points : 0;
  const totalDisplay = stage1Num + (phase === "fly" || phase === "result" ? flightScore : 0);
  const hudTimeSec = baseTimeSec + elapsedFlight;
  const hudTimeStr = formatMmSsFromSeconds(hudTimeSec);

  return (
    <div className="broom-flight-root">
      {rulesModalOpen ? (
        <div
          className="broom-flight-rules-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="broom-flight-rules-modal-title"
        >
          <div className="broom-flight-rules-modal-panel">
            <h2 id="broom-flight-rules-modal-title" className="broom-flight-rules-modal-title">
            Broom Flight Trial

            </h2>
            <ul className="broom-flight-rules-modal-list">
              {FLIGHT_RULES_ITEMS.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <button
              type="button"
              className="broom-flight-rules-modal-close"
              onClick={() => setRulesModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

      <header className="broom-flight-hud" aria-label="Flight status">
        <div className="broom-flight-stat">
          <span className="broom-flight-stat-label"></span>
          <span className="broom-flight-stat-value" style={{ visibility: 'hidden' }}>
            <img src={timeIcon} alt="" width={22} height={22} style={{ verticalAlign: "middle", marginRight: 4 }} />
            {hudTimeStr}
          </span>
        </div>
        <div className="broom-flight-hud-mid" style={{ visibility: 'hidden' }}>
          <h1 className="broom-flight-hud-title">Golden Snitch pursuit</h1>
          <p className="broom-flight-hud-sub" aria-live="polite">
            {hudLine}
          </p>
        </div>
        <div className="broom-flight-stat broom-flight-stat--right">
          <span className="broom-flight-stat-label"></span>
          <span className="broom-flight-stat-value">
            <button
              type="button"
              className="broom-flight-rules-help"
              onClick={() => setRulesModalOpen(true)}
              aria-label="Show flight rules"
            >
              ?
            </button>
            <img src={pointIcon} alt="" width={22} height={22} style={{ verticalAlign: "middle", marginRight: 4 }} />
            {totalDisplay}
          </span>
        </div>
      </header>

      <div className="broom-flight-stage-wrap">
        <div
          ref={stageRef}
          className="broom-flight-stage"
          onPointerDown={onStagePointer}
          role="application"
          aria-label="Broom flight — tap or space to rise"
        >
          <div ref={worldRef} className="broom-flight-world">
            {MONSTER_CONFIG.map((mon, mi) => (
              <img
                key={mon.id}
                ref={(el) => {
                  monsterRefs.current[mi] = el;
                }}
                className="broom-flight-monster"
                src={monsterH1}
                alt=""
                draggable={false}
                style={{
                  left: mon.anchorX,
                  top: `${mon.yRatio * 100}%`,
                }}
              />
            ))}
            {hoops.map((h) => (
              <img
                key={`p-${h.id}`}
                className="broom-flight-pillar"
                src={h.pillarSrc}
                alt=""
                draggable={false}
                style={{ left: h.x - 160 }}
              />
            ))}
            {/* ── COINS: positioned at the visual center of each ring oval ── */}
            {hoops.map((h) => (
              <div
                key={`coin-${h.id}`}
                ref={(el) => { coinRefs.current[h.id] = el; }}
                className="broom-flight-coin"
                style={{ left: h.x, top: h.coinY }}
                aria-hidden
              >
                <div className="broom-flight-coin__inner">
                  <div className="broom-flight-coin__face broom-flight-coin__face--front" />
                  <div className="broom-flight-coin__face broom-flight-coin__face--back" />
                </div>
              </div>
            ))}
            <img
              className="broom-flight-pillar broom-flight-pillar--finish"
              src={pillar1}
              alt=""
              draggable={false}
              style={{ left: FINISH_PILLAR_X }}
            />
          </div>
          <div ref={worldRingsRef} className="broom-flight-world-rings broom-flight-world-rings--back" aria-hidden>
            {hoops.map((h) => (
              <div
                key={`r-${h.id}`}
                ref={(el) => {
                  hoopWrapRefs.current[h.id] = el;
                }}
                className="broom-flight-hoop-wrap"
                style={{ left: h.x }}
              >
                <img
                  src={h.ringSrc}
                  className="broom-flight-hoop broom-flight-hoop--back"
                  alt=""
                  draggable={false}
                />
              </div>
            ))}
          </div>
          <div className="broom-flight-vignette" />

          <canvas
            ref={snitchTrailCanvasRef}
            className="broom-flight-effect-canvas broom-flight-snitch-trail-canvas"
            aria-hidden
          />

          {phase === "fly" ? (
            <div
              ref={playerRef}
              className="broom-flight-player"
              data-hit-pulse={playerHitPulse > 0 ? playerHitPulse : undefined}
            >
              <img
                ref={playerImgRef}
                src={char1}
                alt=""
                draggable={false}
                width={200}
                height={200}
                style={{ width: "100%", height: "auto" }}
              />
            </div>
          ) : null}
          <div ref={hoopFrontsRef} className="broom-flight-hoop-fronts" aria-hidden>
            {hoops.map((h) => (
              <div
                key={`rf-${h.id}`}
                ref={(el) => {
                  hoopFrontWrapRefs.current[h.id] = el;
                }}
                className="broom-flight-hoop-wrap broom-flight-hoop-wrap--front"
                style={{ left: h.x }}
              >
                <img
                  src={h.ringSrc}
                  className="broom-flight-hoop broom-flight-hoop--front"
                  alt=""
                  draggable={false}
                />
              </div>
            ))}
          </div>

          <canvas
            ref={glitterCanvasRef}
            className="broom-flight-effect-canvas broom-flight-glitter-canvas"
            aria-hidden
          />

          {phase === "fly" ? (
            <div ref={snitchRef} className="broom-flight-snitch" aria-hidden>
              <span className="broom-flight-snitch__wing broom-flight-snitch__wing--l" />
              <span className="broom-flight-snitch__wing broom-flight-snitch__wing--r" />
              <span className="broom-flight-snitch__ball" />
            </div>
          ) : null}
          {phase === "fly" ? (
            <div className="broom-flight-flap-hint">Tap / Space — rise · gravity pulls you down</div>
          ) : null}
          {toast ? (
            <div
              className={`broom-flight-toast${/snitch caught/i.test(toast) ? " broom-flight-toast--snitch-caught" : ""}`}
            >
              {toast}
            </div>
          ) : null}
          {catchFlash ? <div className="broom-flight-catch-flash" /> : null}
        </div>

        {phase === "intro" ? (
          <div className="broom-flight-overlay">
            <div
              className="broom-flight-panel"
              style={{
                backgroundImage: `linear-gradient(rgba(255,252,245,0.82), rgba(255,249,235,0.9)), url(${artboardPanel})`,
              }}
            >
              <h2>{introCopy}</h2>
              {introStep >= 2 ? (
                <p>The skies are calling… The Golden Snitch is already in motion.</p>
              ) : null}
              {introStep >= 2 ? (
                <div className="broom-flight-panel__actions">
                  <button type="button" className="broom-flight-btn" onClick={() => setPhase("fly")}>
                    Begin flight
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {phase === "result" ? (
          <div className="broom-flight-overlay">
            <div
              className="broom-flight-panel"
              style={{
                backgroundImage: `linear-gradient(rgba(255,252,245,0.85), rgba(255,249,235,0.93)), url(${artboardPanel})`,
              }}
            >
              <h2>{resultLines.primary}</h2>
              <p>{resultLines.secondary}</p>
              {resultLines.tertiary ? <p>{resultLines.tertiary}</p> : null}
              <div className="broom-flight-panel__actions">
                <button type="button" className="broom-flight-btn" onClick={goThankYou}>
                  Continue
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default BroomFlightGame;
